import type { DiscrepancyCardViewModel } from "./view-model";

/** Какой layout карточки рендерить (фича 024). */
export type CardVariant = "aggregated" | "matrix" | "grand-total";

/**
 * Решает, какой layout карточки использовать для конкретного Discrepancy.
 *
 * **`grand-total`** — для RC-G1 (`level === "grand-total"`). Гранд-тотал по
 * всему набору: показываем Σ нарядов / Σ актов / Δ, отдельной таблицей —
 * suspect-коды (где Σ wo ≠ Σ act), совпавшие коды свёрнуты по умолчанию.
 * Не рендерится через matrix-карточку, потому что matrix дизайнилась под
 * per-position сравнение, а тут — set-level (FR-011/012/013).
 *
 * **`matrix`** — когда у расхождения есть источники С ОБЕИХ сторон (наряды И
 * акты) на per-position уровне. Только в этом случае нужна matrix-таблица с
 * группировкой по сторонам (violet наряды / sky акты) и Δ-row внизу. Сюда
 * попадают:
 * - RC-P3/P4/P6 (имя/единица/расценка отличаются — обе стороны есть)
 * - RC-P5/P7 (объём/стоимость расходятся — обе стороны есть)
 * - Любая комбинация перечисленных правил на одном коде.
 *
 * **`aggregated`** — когда источники только с ОДНОЙ стороны (или ни с одной).
 * Aggregated-карточка показывает single-table источников + Σ-strip. Сюда
 * попадают:
 * - RC-P1 (лишняя в акте — только actSources)
 * - RC-P2 (недовыполнение — только workOrderSources)
 * - RC-P9 (бескодовая в акте — только actSources)
 * - RC-P10 (бескодовая в наряде — только workOrderSources)
 *
 * Логика «оба источника есть → matrix» решает проблему, при которой single-rule
 * RC-P7 проваливался в aggregated и терял одну сторону (subtitle обещал
 * «8 нарядов ↔ 5 актов», а UI показывал только одну сторону).
 */
export function pickCardVariant(d: DiscrepancyCardViewModel): CardVariant {
	if (d.level === "grand-total") {
		return "grand-total";
	}
	if (d.workOrderSources.length > 0 && d.actSources.length > 0) {
		return "matrix";
	}
	return "aggregated";
}
